import React, { useEffect, useMemo, useRef, useState } from "react"

import { NotificationManager } from "react-notifications"

import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"

import EventEmitter from "src/EventEmitter"
import createBlob from "src/createBlob"
import { RunMutation } from "src/types"

import { gql, useMutation, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import EditableDisplay from "components/Forms/EditableDisplay"
import { Loading } from "components/Icons"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import NotificationContainer from "components/NotificationContainer"

import DuplicateLessonPlanButton from "../DuplicateButton"

import LessonTopic from "./LessonTopic"
import NewTopic from "./NewTopic"
import lessonTopicsFragment from "./lessonTopicsFragment"

const LessonPlanShow = ({ id }) => {
  const csvExportDownloadLink = useRef<HTMLAnchorElement>(null)
  const [exporting, setExporting] = useState(false)
  const [csvExportUrl, setCsvExportUrl] = useState("")
  const [result] = useQuery({ query: lessonPlanQuery, variables: { id } })

  const [, updateLessonPlan] = useMutation(updateLessonPlanMutation) as [
    any,
    RunMutation
  ]
  const [, removeLessonPlan] = useMutation(removeLessonPlanMutation) as [
    any,
    RunMutation
  ]
  const [, exportLessonPlan] = useMutation(exportLessonPlanMutation) as [
    any,
    RunMutation
  ]

  useEffect(() => {
    if (csvExportUrl === "") return
    csvExportDownloadLink.current?.click()
  }, [csvExportUrl])

  const channelProps = useMemo(() => {
    return { id }
  }, [id])

  useWebsocket({
    channelName: "LessonPlanChannel",
    channelProps,
  })

  useEffect(() => {
    const handler = data => {
      setExporting(false)
      setCsvExportUrl(createBlob(data.csv, "text/csv"))
    }
    EventEmitter.subscribe("exportComplete", handler)
    return () => EventEmitter.unsubscribe("exportComplete", handler)
  }, [])

  const csvExport = () => {
    setExporting(true)
    exportLessonPlan({ id }).then(result => {
      if (result.error) {
        setExporting(false)
        NotificationManager.error(`An error occurred: ${result.error.message}`)
        return
      }
      const { errorMessages } = result.data.exportLessonPlan
      if (errorMessages) {
        NotificationManager.error(`An error occurred: ${errorMessages}`)
        setExporting(false)
      }
    })
  }

  const handleRemoveLessonPlan = () => {
    removeLessonPlan({ id }).then(result => {
      if (result.error) {
        NotificationManager.error(result.error.message)
      } else {
        window.location.href = result.data.removeLessonPlan.redirectTo
      }
    })
  }

  const handleUpdateLessonPlan = ({ value }) => {
    return updateLessonPlan({ id, name: value }).then(result => {
      if (result.error) {
        NotificationManager.error(result.error.message)
      }
    })
  }

  return (
    <WithLoadingIndicator result={result}>
      {({ data }) => (
        <div>
          <NotificationContainer />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <EditableDisplay
              displayValue={data.lessonPlan.name}
              save={handleUpdateLessonPlan}
              height="auto"
            />
            <div className="flex items-center">
              <ModalWithProvidedBody
                modalTitle="Remove Lesson Plan"
                buttonText="Remove Lesson Plan"
                buttonClassName="btn btn-danger"
              >
                {({ closeModal }) => (
                  <>
                    <div className="modal-body">
                      Are you sure? This is a VERY destructive, irreversible
                      action! This action will fail if any students are
                      currently assigned to this plan or have previously
                      completed assignments.
                    </div>
                    <div className="modal-footer">
                      <div className="flex w-full justify-between">
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleRemoveLessonPlan}
                        >
                          Yes, remove it and all associated data
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </ModalWithProvidedBody>
              <DuplicateLessonPlanButton
                id={id}
                buttonClassNames="btn btn-info"
              />
              <button
                className="btn btn-success"
                onClick={csvExport}
                disabled={exporting}
              >
                Export to CSV
                {exporting && (
                  <div className="ml-4 inline-block">
                    <Loading />
                  </div>
                )}
              </button>
              <a
                ref={csvExportDownloadLink}
                href={csvExportUrl}
                download={`${data.lessonPlan.name} export.csv`}
                className="hidden"
              >
                csv
              </a>
            </div>
          </div>

          <div className="flex items-center">
            <NewTopic lessonPlanId={id} />
          </div>
          <ul className="mt-4 list-none space-y-4 p-0">
            {data.lessonPlan.lessonTopics.map(topic => (
              <LessonTopic
                key={topic.id}
                topic={topic}
                first={topic === data.lessonPlan.lessonTopics[0]}
                last={
                  topic ===
                  data.lessonPlan.lessonTopics[
                    data.lessonPlan.lessonTopics.length - 1
                  ]
                }
              />
            ))}
          </ul>
        </div>
      )}
    </WithLoadingIndicator>
  )
}

const lessonPlanQuery = gql`
  ${lessonTopicsFragment}
  query lessonPlanQuery($id: ID!) {
    lessonPlan(id: $id) {
      name
      lessonTopics {
        ...LessonTopicsFields
      }
    }
  }
`

const exportLessonPlanMutation = gql`
  mutation ExportLessonPlanMutation($id: ID!) {
    exportLessonPlan(lessonPlanId: $id) {
      errorMessages
    }
  }
`

const updateLessonPlanMutation = gql`
  mutation UpdateLessonPlanMutation($id: ID!, $name: String!) {
    updateLessonPlan(id: $id, name: $name) {
      lessonPlan {
        name
      }
    }
  }
`

const removeLessonPlanMutation = gql`
  mutation RemoveLessonPlanMutation($id: ID!) {
    removeLessonPlan(id: $id) {
      redirectTo
    }
  }
`

export default LessonPlanShow
